import React from 'react'

/**
 * Props for notification icon components
 */
export interface NotificationIconProps {
  /** Icon size in pixels (default: 16) */
  size?: number
  /** Icon color (default: #F2B400) */
  color?: string
}

/**
 * RefreshIcon component - renders a refresh/reload SVG icon
 * Used for refresh notifications functionality
 *
 * @param {NotificationIconProps} props - Component props
 * @returns {JSX.Element} Refresh icon SVG
 */
export const RefreshIcon: React.FC<NotificationIconProps> = ({
  size = 16,
  color = '#F2B400',
}) => {
  console.log('🔄 RefreshIcon: Rendering refresh icon', { size })

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width={`${size}px`}
      height={`${size + 1}px`}
      viewBox='0 0 16 17'
      fill='none'
    >
      <path
        d='M11.7633 4.73334C10.7966 3.76667 9.46992 3.16667 7.99659 3.16667C5.04992 3.16667 2.66992 5.55334 2.66992 8.5C2.66992 11.4467 5.04992 13.8333 7.99659 13.8333C10.4833 13.8333 12.5566 12.1333 13.1499 9.83334H11.7633C11.2166 11.3867 9.73659 12.5 7.99659 12.5C5.78992 12.5 3.99659 10.7067 3.99659 8.5C3.99659 6.29334 5.78992 4.50001 7.99659 4.50001C9.10326 4.50001 10.0899 4.96001 10.8099 5.68667L8.66326 7.83334H13.3299V3.16667L11.7633 4.73334Z'
        fill={color}
      />
    </svg>
  )
}

interface CheckmarkIconProps extends NotificationIconProps {
  color?: string
}

/**
 * CheckmarkIcon component - renders a checkmark SVG icon
 * Used for "mark all as read" functionality
 *
 * @returns {JSX.Element} Checkmark icon SVG
 */
export const CheckmarkIcon: React.FC<CheckmarkIconProps> = ({
  size = 16,
  color = '#F2B400',
}) => {
  console.log('🔄 CheckmarkIcon: Rendering checkmark icon', { size, color })

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width={`${size}px`}
      height={`${size + 1}px`}
      viewBox='0 0 16 17'
      fill='none'
    >
      <path
        d='M11.9767 4.97L11.0367 4.03L6.81 8.25667L7.75 9.19667L11.9767 4.97ZM14.8033 4.03L7.75 11.0833L4.96333 8.30333L4.02333 9.24333L7.75 12.97L15.75 4.97L14.8033 4.03ZM0.25 9.24333L3.97667 12.97L4.91667 12.03L1.19667 8.30333L0.25 9.24333Z'
        fill={color}
      />
    </svg>
  )
}

/**
 * InvitationsIcon component - renders an envelope/mail icon
 * Used for invitation-type notifications
 *
 * @returns {JSX.Element} Invitations icon SVG
 */
export const InvitationsIcon: React.FC = () => {
  console.log('🔄 InvitationsIcon: Rendering invitations icon')

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
    >
      <path
        d='M13.3335 2.66666H2.66683C1.9335 2.66666 1.34016 3.26666 1.34016 3.99999L1.3335 12C1.3335 12.7333 1.9335 13.3333 2.66683 13.3333H13.3335C14.0668 13.3333 14.6668 12.7333 14.6668 12V3.99999C14.6668 3.26666 14.0668 2.66666 13.3335 2.66666ZM12.6668 12H3.3335C2.96683 12 2.66683 11.7 2.66683 11.3333V5.33332L7.2935 8.22666C7.72683 8.49999 8.2735 8.49999 8.70683 8.22666L13.3335 5.33332V11.3333C13.3335 11.7 13.0335 12 12.6668 12ZM8.00016 7.33332L2.66683 3.99999H13.3335L8.00016 7.33332Z'
        fill='white'
      />
    </svg>
  )
}

/**
 * SocialFeedIcon component - renders a newspaper/feed icon
 * Used for social feed notifications
 *
 * @returns {JSX.Element} Social feed icon SVG
 */
export const SocialFeedIcon: React.FC = () => {
  console.log('🔄 SocialFeedIcon: Rendering social feed icon')

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='17'
      viewBox='0 0 16 17'
      fill='none'
    >
      <path
        d='M14.1002 2.72748L13.5535 3.27415L12.9202 2.63415C12.6602 2.37415 12.2402 2.37415 11.9735 2.63415L11.3335 3.27415L10.6935 2.63415C10.4335 2.37415 10.0068 2.37415 9.74683 2.63415L9.1135 3.27415L8.4735 2.63415C8.2135 2.37415 7.7935 2.37415 7.5335 2.63415L6.8935 3.27415L6.2535 2.63415C5.9935 2.37415 5.5735 2.37415 5.30683 2.63415L4.66683 3.27415L4.02683 2.63415C3.76683 2.37415 3.34016 2.37415 3.08016 2.63415L2.44683 3.27415L1.90016 2.72748C1.6935 2.52081 1.3335 2.66748 1.3335 2.96748V12.8275C1.3335 13.5608 1.9335 14.1608 2.66683 14.1608H13.3335C14.0668 14.1608 14.6668 13.5608 14.6668 12.8275V2.96748C14.6668 2.66748 14.3068 2.52081 14.1002 2.72748ZM7.3335 12.8275H2.66683V8.82748H7.3335V12.8275ZM13.3335 12.8275H8.66683V11.4941H13.3335V12.8275ZM13.3335 10.1608H8.66683V8.82748H13.3335V10.1608ZM13.3335 7.49415H2.66683V5.49415H13.3335V7.49415Z'
        fill='white'
      />
    </svg>
  )
}

/**
 * GameStatusIcon component - renders a game status update icon
 * Used for game-related notifications (finished, status updates, rescheduled, upcoming, scored)
 *
 * @returns {JSX.Element} Game status icon SVG
 */
export const GameStatusIcon: React.FC = () => {
  console.log('🔄 GameStatusIcon: Rendering game status icon')

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='17'
      viewBox='0 0 16 17'
      fill='none'
    >
      <path
        d='M1.3335 11.9333V13.9333H2.66683V11.2667H2.00016C1.6335 11.2667 1.3335 11.5667 1.3335 11.9333Z'
        fill='white'
      />
      <path
        d='M6.00016 11.2667H3.3335V13.9333L6.46016 13.9267C6.7135 13.9267 6.94016 13.7867 7.0535 13.56L7.6335 12.2933L6.5735 9.97333L6.00016 11.2667Z'
        fill='white'
      />
      <path
        d='M14.4735 11.46C14.3535 11.34 14.1868 11.2667 14.0002 11.2667H13.3335V13.9333H14.6668V11.9333C14.6668 11.7467 14.5935 11.58 14.4735 11.46Z'
        fill='white'
      />
      <path
        d='M9.06683 9.16L11.7668 3.26666H9.5335L8.36016 5.91333L8.0335 6.64666L8.00016 6.74L6.46683 3.26666H4.2335L7.94683 11.3733L8.00016 11.4933L8.94683 13.56C9.06016 13.7867 9.28683 13.9267 9.54016 13.9267L12.6668 13.9333V11.2667H10.0002L9.06683 9.16Z'
        fill='white'
      />
    </svg>
  )
}

/**
 * MoreVerticalIcon component - renders a vertical ellipsis (three dots) icon
 * Used for options/menu buttons in notifications
 *
 * @param {NotificationIconProps} props - Component props
 * @returns {JSX.Element} More vertical icon SVG
 */
export const MoreVerticalIcon: React.FC<NotificationIconProps> = ({
  size = 24,
  color = '#C1C2C3',
}) => {
  console.log('🔄 MoreVerticalIcon: Rendering more vertical icon', {
    size,
    color,
  })

  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
    >
      <path
        d='M12 8C13.1 8 14 7.1 14 6C14 4.9 13.1 4 12 4C10.9 4 10 4.9 10 6C10 7.1 10.9 8 12 8ZM12 10C10.9 10 10 10.9 10 12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12C14 10.9 13.1 10 12 10ZM12 16C10.9 16 10 16.9 10 18C10 19.1 10.9 20 12 20C13.1 20 14 19.1 14 18C14 16.9 13.1 16 12 16Z'
        fill={color}
      />
    </svg>
  )
}
